import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Paragraph, Text } from '@zillow/constellation';
import {
    Center, Col, HR, Row,
} from '@zillow/zrs-comet';


import PostingActions from 'app/shared/actions/PostingActions';
import PropertiesActions from 'app/shared/actions/PropertiesActions';

import propertyUtils from 'app/shared/utils/propertyUtils';

import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import WizardActionFooter from 'app/shared/components/WizardActionFooter';

import { ADD_RENT_GUARANTEE_FEE } from '../constants';

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
    onGoToNextStep: PropTypes.func.isRequired,
    propertyId: PropTypes.string.isRequired,
    progressStepper: PropTypes.node.isRequired,
};
const defaultProps = {};

const getFormattedAddress = (city,
    state,
    street,
    unit,
    zip) => {
    const tempAddress = propertyUtils.formatAddress({
        street,
        city,
        state,
        unit,
        zip,
    });

    return `${tempAddress.formattedAddress} ${zip}`;
};

const Confirmation = ({
    onGoToNextStep,
    onGoToPrevStep,
    propertyId,
    progressStepper,
}) => {
    const dispatch = useDispatch();
    const {
        rentGuaranteeFee,
        tenantPaymentAmount,
        landlordReceiptAmount,
        securityDepositAmount,
        isSecurityDepositRequired,
        paymentMethod,
    } = useSelector(state => state.rentGuarantee);
    const {
        city,
        state,
        street,
        unit,
        zip,
    } = useSelector(state => state.properties.currentProperty);
    const [formattedAddress, setFormattedAddress] = useState(getFormattedAddress(city,
        state,
        street,
        unit,
        zip));

    useEffect(() => {
        if (!city || !state || !street || !unit || !zip) {
            dispatch(PostingActions.property.getDetails(propertyId)).then((response) => {
                const {
                    city,
                    state,
                    street,
                    unit,
                    zip,
                } = response;

                setFormattedAddress(getFormattedAddress(city,
                    state,
                    street,
                    unit,
                    zip));
            });
        }
    }, [propertyId]);

    const handleGoNextStep = () => {
        dispatch(PropertiesActions.enableRentGuarantee(propertyId,
            !isSecurityDepositRequired, paymentMethod === ADD_RENT_GUARANTEE_FEE));
        onGoToNextStep();
    };

    const infoSections = [{
        section: 'Rent Guarantee fee',
        value: `${Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumSignificantDigits: 2,
        }).format(rentGuaranteeFee)} per month`,
        description: `Automatically deducted after the tenant pays ${Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumSignificantDigits: 5,
        }).format(tenantPaymentAmount)}, and ${Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumSignificantDigits: 5,
        }).format(landlordReceiptAmount)} will be deposited in your bank account.`,
    }, {
        section: 'Security deposit',
        value: isSecurityDepositRequired ? Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumSignificantDigits: 5,
        }).format(securityDepositAmount) : 'Waived',
        description: isSecurityDepositRequired ? 'You would have to use deposit first before the claim.' :
            'Your rental will be listed as a Zero Deposit property.',
    }, {
        section: 'Online tools',
        value: 'Enabled',
        description: 'You’ll use Zillow’s online application, lease, and payment tools.',
    }];

    const renderSubtitle = () => (
        <>
            <Paragraph>Here’s your Rent Guarantee setup summary for</Paragraph>
            <Paragraph fontType="bodyHeading" fontColor="textMedium">{formattedAddress}</Paragraph>
        </>
    );

    return (
        <WizardBodyTemplate
            title="Review your setup"
            subtitle={renderSubtitle()}
            progressStepper={progressStepper}
            footer={(
                <WizardActionFooter
                    nextLabel="Confirm"
                    onBackClick={onGoToPrevStep}
                    onNextClick={handleGoNextStep}
                />
            )}
        >
            {infoSections.map(section => (
                <Row key={section.section} data-test={section.section}>
                    <HR />
                    <Col xs={6}><Text>{section.section}</Text></Col>
                    <Col xs={6}>
                        <Paragraph fontType="bodyHeading">
                            {section.value}
                        </Paragraph>
                        <Text fontType="bodySmall">
                            {section.description}
                        </Text>
                    </Col>
                </Row>
            ))}
            <Row>
                <HR />
                <Center>
                    Rent Guarantee will be activated when the tenant makes their first rent payment.
                </Center>
            </Row>
        </WizardBodyTemplate>
    );
};

Confirmation.propTypes = propTypes;
Confirmation.defaultProps = defaultProps;

export default Confirmation;
